import { defineStore } from "pinia";
import { unifiedApi } from "@/common";
import { computed, ref } from "vue";
import { handleApiError } from "@/common/handleApiError";
import { logEvent } from "@/common/logger";
import { AxiosError } from "axios";
import { IPendingVerificationEmailResponse, ISecurityQuestions } from "@/common/api/unifiedPortal/interfaces";

export const usePersonalInfoStore = defineStore("personalInfo", () => {
  const pendingVerificationEmail = ref<string>("");
  const showVerifyEmailAddressModal = ref("");
  const addCreditCardSuccess = ref(false);
  const questions = ref<ISecurityQuestions | null>(null);
  const is2FaEnabled = ref(false);
  const phoneNumber = ref("");
  const isVerified = ref(false);

  const areSecurityQuestionsSet = computed(() => {
    return (
      questions.value?.securityQuestionOne &&
      questions.value?.securityQuestionTwo &&
      questions.value?.securityQuestionThree
    );
  });

  const areSecurityQuestionsAnd2FaSet = computed(() => {
    return areSecurityQuestionsSet.value && is2FaEnabled.value;
  });

  /** Retrieves any pending email verifications so we can get the current email user is using */
  async function checkPendingEmailVerification() {
    try {
      const getPendingVerificationResponse: IPendingVerificationEmailResponse = (
        await unifiedApi.getPendingVerificationEmail()
      ).data;

      pendingVerificationEmail.value = getPendingVerificationResponse.pendingEmail;
    } catch (error) {
      logEvent(`Resulted in not OK ${error}`, `usePersonalInfoStore/getPendingVerification`);
      handleApiError(error as AxiosError);
    }
  }

  /** Retrieves security phone information and 2FA status */
  async function getSecurityPhoneFor2FA() {
    try {
      const securityPhone = (await unifiedApi.getUserSecurityPhone()).data;
      logEvent("SecurityPhone", "usePersonalInfoStore", securityPhone?.phoneNumber);
      phoneNumber.value = securityPhone?.phoneNumber ?? "";
      isVerified.value = !!securityPhone?.phoneNumberValidatedDate;
      is2FaEnabled.value = securityPhone?.isUsedForMultiFactorAuthentication ?? false;
      return phoneNumber.value;
    } catch (error) {
      logEvent(`Resulted in not OK ${error}`, `usePersonalInfoStore/getSecurityPhoneFor2FA`);
      handleApiError(error as AxiosError);
      return "";
    }
  }

  return {
    pendingVerificationEmail,
    showVerifyEmailAddressModal,
    checkPendingEmailVerification,
    addCreditCardSuccess,
    questions,
    areSecurityQuestionsSet,
    is2FaEnabled,
    phoneNumber,
    isVerified,
    getSecurityPhoneFor2FA,
    areSecurityQuestionsAnd2FaSet,
  };
});
